<template>
  <BSkeletonWrapper
    :loading="loading"
  >
    <template #loading>
      <BCard>
        <BCardTitle>
          <div class="h6">
            <BSkeleton
              width="75%"
              height="23px"
            />
          </div>
        </BCardTitle>

        <BCardText>
          <div class="text-nowrap text-truncate">
            <BSkeleton
              width="100%"
              height="16px"
            />
          </div>
        </BCardText>

        <BCardText>
          <BContainer>
            <BRow>
              <BSkeletonImg
                height="200px"
                class="col-12 border border-light border-lg"
                no-aspect
              />
              <BSkeletonImg
                height="200px"
                class="col-6 border border-light border-lg"
                no-aspect
              />
              <BSkeletonImg
                height="200px"
                class="col-6 border border-light border-lg"
                no-aspect
              />
            </BRow>
          </BContainer>
        </BCardText>

        <BCardText>
          <slot name="footer-loader" />
        </BCardText>
      </BCard>
    </template>
    <BCard>

      <BCardText>
        <div class="d-flex gap-4 mb-3">
          <BAvatar
            v-if="officer?.photo_url"
            :src="computedProfilePath(officer.photo_url)"
            variant="primary"
            size="36px"
          />
          <BAvatar
            v-else
            variant="primary"
            size="36px"
            :text="String(officer?.first_name).charAt(0) + String(officer?.last_name).charAt(0)"
          />
          <div class="d-flex flex-column justify-content-center">
            <div
              class="font-weight-bolder"
              style="font-size: 0.8rem;"
            >
              {{ officer?.first_name }} {{ officer?.last_name }}
            </div>
            <div
              class="mt-1 text-dark"
              style="font-size: 0.7rem;"
            >
              {{ dateTimeShortFormatter(details.created_at) }}
            </div>
          </div>
        </div>
      </BCardText>

      <BCardText>
        <div class="d-flex flex-column justify-content-center mb-1">
          <div
            class="font-weight-bolder"
            style="font-size: 0.8rem;"
          >
            {{ details?.activity_name }}
          </div>
          <div
            class="mt-1"
            style="font-size: 0.7rem;"
          >
            For <b>{{ details?.crop_name }}</b>, held at <b>{{ details?.barangay_name }}, {{ details?.municipality_name }}, {{ details?.province_name }}</b>
          </div>
        </div>
      </BCardText>

      <BCardText>
        <div
          class="text-nowrap text-truncate"
          style="font-size: 14px;"
        >
          {{ details?.remarks }}
        </div>
      </BCardText>

      <BCardText>
        <BContainer>
          <BRow
            v-if="attachments.length <= 0"
          >
            <BCol
              cols="12"
              :style="{
                backgroundImage: `url(/img/invalid-image.png)`,
                // backgroundImage: `url(${attachment.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                height: attachmentDivider(0).height
              }"
              class="border border-light border-lg"
            />
          </BRow>
          <BRow
            v-else
          >
            <BCol
              v-for="(attachment, index) in attachments.filter((_, i) => attachments.length <= 4 ? i <= 3 : i <= 2)"
              :key="index"
              :cols="attachmentDivider(index).cols"
              :style="{
                backgroundImage: `url(${attachmentUrl(attachment)})`,
                // backgroundImage: `url(${attachment.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                height: attachmentDivider(index).height,
                minHeight: attachmentDivider(index).height
              }"
              class="border border-light border-lg cursor-pointer"
              @click.prevent="$emit('show:attachment', index)"
            />
            <BCol
              v-if="attachments.length >= 5"
              cols="4"
              :style="{
                backgroundImage: `linear-gradient(0deg, rgba(100, 100, 100, 0.7), rgba(100, 100, 100, 0.5)), url(${attachmentUrl(attachments[3])})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                height: attachmentDivider(4).height,
                minHeight: attachmentDivider(4).height
              }"
              class="border border-light border-lg d-flex justify-content-center align-items-center"
            >
              <BButton
                variant="link"
                @click.prevent="$emit('more:attachments', { title, description, index })"
              >
                <h5 class="text-white font-weight-bold">
                  {{ attachments.length - 3 }}+ more
                </h5>
              </BButton>
            </BCol>
          </BRow>
        </BContainer>
      </BCardText>

      <BCardText>
        <slot name="footer" />
      </BCardText>
    </BCard>
  </BSkeletonWrapper>
</template>
<script>
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MxMisc from '@/mixins/misc'

export default {
  name: 'VASMOfficerEvent',

  mixins: [
    MxFormatter,
    MxMisc,
    MxImageUrl
  ],

  props: {
    loading: {
      type: Boolean,
      default: () => false
    },
    officer: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        first_name: '',
        last_name: '',
        photo_url: ''
      })
    },
    details: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        province_name: '',
        municipality_name: '',
        barangay_name: '',
        customer_name: '',
        products: [],
        activity_name: '',
        crop_name: ''
      })
    },
    attachments: {
      type: Array,
      required: true,
      default: () => []
    },
    attachmentPath: {
      type: String,
      required: true,
      default: () => ''
    }
  },

  emits: [
    'more:attachments',
    'show:attachment'
  ],

  methods: {

    computedProfilePath (photoUrl) {
      return `${process.env.VUE_APP_API_BASE_URL}/${photoUrl}`
    },

    attachmentUrl (file) {
      return this.transformToImageUrl(`${this.attachmentPath}/${file.file_hashed_name}`)
    },

    attachmentDivider (index) {
      const isMobile = this.isMobile()

      const smallerScreenWidth = isMobile ? '150px' : '200px'
      const largeScreenWidth = isMobile ? '300px' : '400px'

      switch (this.attachments.length) {
        case 1: {
          return {
            cols: 12,
            height: largeScreenWidth
          }
        }
        case 2: {
          return {
            cols: 12,
            height: smallerScreenWidth
          }
        }
        case 3: {
          if (Number(index) === 0) {
            return {
              cols: 12,
              height: smallerScreenWidth
            }
          }

          return {
            cols: 6,
            height: smallerScreenWidth
          }
        }
        default: {
          if (Number(index) === 0) {
            return {
              cols: 12,
              height: smallerScreenWidth
            }
          }

          return {
            cols: 4,
            height: smallerScreenWidth
          }
        }
      }
    }
  }
}
</script>
